import { useMemo } from "react";
import GuideHint from "../GetStartedGuide/GuideHint";
import { DEPOSIT_GUIDE_HINTS } from "../GetStartedGuide/depositGuideConfig";

/**
 * Encapsulates the deposit "get started" guide hint logic for TransferModal.
 *
 * `showDepositGuide` is the single flag controlling whether hints can appear
 * at all. `guideSkipped` is separate — it tracks the user dismissing the
 * guide mid-flow, and is still respected even when showDepositGuide is true.
 */
export function useGuideHint({
  showDepositGuide,
  guideSkipped,
  setGuideSkipped,
  activeTab,
  modalPhase,
  currentStep,
  depositStep2Phase,
  onGuideBuyCrypto,
  onGuideComplete,
}) {
  const activeGuideHint = useMemo(() => {
    if (
      !showDepositGuide ||
      guideSkipped ||
      activeTab !== "Deposit" ||
      modalPhase !== "form"
    ) {
      return null;
    }

    if (currentStep === 1) return DEPOSIT_GUIDE_HINTS.selectCoin;
    if (currentStep === 2 && depositStep2Phase === "network") {
      return DEPOSIT_GUIDE_HINTS.selectNetwork;
    }
    if (currentStep === 3) return DEPOSIT_GUIDE_HINTS.copyAddress;

    return null;
  }, [
    showDepositGuide,
    guideSkipped,
    activeTab,
    modalPhase,
    currentStep,
    depositStep2Phase,
  ]);

  const hideGuideHints = () => setGuideSkipped(true);

  const completeGuide = () => {
    hideGuideHints();
    onGuideComplete?.();
  };

  const buildStepGuideHint = (hint) =>
    hint
      ? {
          title: `Step ${hint.step}: ${hint.title}`,
          description: hint.description,
          primaryAction: hint.primaryAction,
          secondaryAction: hint.secondaryAction,
          showPointer: hint.showPointer,
          position: hint.position,
          onSkip: hideGuideHints,
          onDone: completeGuide,
          onBuyCrypto: () => {
            hideGuideHints();
            onGuideBuyCrypto?.();
          },
        }
      : null;

  const selectCoinGuideHint = activeGuideHint?.id === "select-coin" ? buildStepGuideHint(activeGuideHint) : null;
  const selectNetworkGuideHint = activeGuideHint?.id === "select-network" ? buildStepGuideHint(activeGuideHint) : null;
  const copyAddressGuideHint = activeGuideHint?.id === "copy-address" ? buildStepGuideHint(activeGuideHint) : null;

  const inlineMobileGuideHint = selectCoinGuideHint || selectNetworkGuideHint || copyAddressGuideHint;
  const mobileGuideDimActive = Boolean(inlineMobileGuideHint);

  const guideHint = activeGuideHint
    ? {
        position: activeGuideHint.position,
        inlineOnMobile: activeGuideHint.inlineOnMobile,
        content: (
          <GuideHint
            title={`Step ${activeGuideHint.step}: ${activeGuideHint.title}`}
            description={activeGuideHint.description}
            position={activeGuideHint.position}
            showPointer={activeGuideHint.showPointer}
            primaryAction={activeGuideHint.primaryAction}
            secondaryAction={activeGuideHint.secondaryAction}
            onSkip={hideGuideHints}
            onDone={completeGuide}
            onBuyCrypto={() => {
              hideGuideHints();
              onGuideBuyCrypto?.();
            }}
          />
        ),
      }
    : null;

  return {
    activeGuideHint,
    guideHint,
    selectCoinGuideHint,
    selectNetworkGuideHint,
    copyAddressGuideHint,
    inlineMobileGuideHint,
    mobileGuideDimActive,
    hideGuideHints,
    completeGuide,
  };
}

export default useGuideHint;